import { Component, Input, OnChanges, SimpleChanges, AfterViewInit, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import {
  Panel,
  RecentlyOrderedItem,
  RecentlyOrderedItemsService,
  TagTab,
  Tab,
  AccountService,
  AccountLoyaltyService,
  Account,
} from 'ngx-web-api';
import { map, distinctUntilChanged, filter, switchMap, take, catchError, takeUntil } from 'rxjs/operators';
import { ThemeService } from '../../core/services/theme.service';
import { Theme } from '../../domain/theme.model';
import { ViewportService } from 'app/core/services/viewport.service';
import { FocusService } from '../../focus.service';
import { ViewPort } from '../../domain/view-port.model';
import { MobileMenu } from 'app/domain/mobile-menu.enum';
import { SlugifyPipe } from '../pipes/slugify.pipe';
import { MenuWrapperService } from 'app/core/services/menu-wrapper.service';
import { of, Subject } from 'rxjs';

@Component({
  selector: 'fts-vertical-categories-menu',
  templateUrl: './vertical-categories-menu.component.html',
  styleUrls: ['./vertical-categories-menu.component.scss'],
})
export class VerticalCategoriesMenuComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input()
  panel: Panel;
  @Input()
  hasLeftMenuIcons: boolean;
  @Output()
  onTabClick: EventEmitter<MobileMenu> = new EventEmitter<MobileMenu>();

  theme: Theme;
  hasRecentlyOrderedItems: boolean;
  hasLoyaltySpecialsItems: boolean = false;
  panelTabs: (Tab | TagTab)[] = [];
  isMobile = false;
  isInTablet = false;
  tabLinksMap: { [key: string]: string } = {};
  private slugifyPipe = new SlugifyPipe();
  headerHeight: number;
  recentlyOrderedLabel = 'RecentlyOrdered';
  loyaltySpecialsLabel = 'LoyaltySpecials';
  private destroy$ = new Subject<void>();

  constructor(
    private recentlyOrderedItemsService: RecentlyOrderedItemsService,
    private themeService: ThemeService,
    public viewPortService: ViewportService,
    private focusService: FocusService,
    private menuService: MenuWrapperService,
    private accountService: AccountService,
    private accountLoyaltyService: AccountLoyaltyService
  ) {
    this.themeService.theme.subscribe(theme => {
      this.theme = theme;
      this.initTabLinks();
    });
    this.recentlyOrderedItemsService.recentlyOrderedItems$
      .pipe(map((recentlyOrderedItems: RecentlyOrderedItem[]) => recentlyOrderedItems && recentlyOrderedItems.length > 0))
      .subscribe((hasRecentlyOrderedItems: boolean) => (this.hasRecentlyOrderedItems = hasRecentlyOrderedItems));
  }

  ngOnInit() {
    this.menuService.headerHeight$.subscribe(headerHeight => (this.headerHeight = headerHeight));
    this.viewPortService.viewport$.pipe(distinctUntilChanged()).subscribe((viewport: ViewPort) => {
      this.isMobile = viewport.isMobile;
      this.isInTablet = viewport.isTablet;
    });
    const loggedInAccount$ = this.accountService.account$.pipe(
      filter((account: Account) => !account.dummy && account.isInstantiated),
      distinctUntilChanged((prev, curr) => prev.id === curr.id)
    );

    loggedInAccount$
      .pipe(
        switchMap(() =>
          this.accountLoyaltyService.loyaltyMembership$.pipe(
            take(1),
            switchMap(membership => this.accountLoyaltyService.getAccountSubscription(membership).pipe(catchError(() => of(null))))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(sub => {
        this.hasLoyaltySpecialsItems = (sub?.loyaltyPerkSpecials ?? []).length > 0;
      });
  }

  ngAfterViewInit() {
    if (this.isMobile) {
      this.focusService.applyFocus('categoryLinksContainer');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showTabIcons(): boolean {
    return this.panel && this.panel.tabs && (this.panel.tabs.some(tab => !!tab.image) || this.panel.tagTabs.some(tagTab => !!tagTab.image));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['panel'] && !!this.panel) {
      this.panelTabs = [...(this.panel.tagTabs || []), ...(this.panel.tabs || [])];
      this.initTabLinks();
    }
  }

  tabClick() {
    this.onTabClick.emit(undefined);
  }

  private initTabLinks() {
    if (this.theme.hasSinglePageMenu) {
      this.panelTabs.forEach(panelTab => {
        this.tabLinksMap[panelTab.name] = `menu/single-page#${this.slugifyPipe.transform(panelTab.name)}`;
      });
      this.recentlyOrderedLabel = 'Recently Ordered';
    } else {
      this.panelTabs.forEach(panelTab => {
        this.tabLinksMap[panelTab.name] = `menu/${encodeURIComponent(panelTab.name)}`;
      });
    }
    const recentlyOrderedLink = this.theme.hasSinglePageMenu
      ? `menu/single-page#${this.slugifyPipe.transform(this.recentlyOrderedLabel)}`
      : `menu/${this.recentlyOrderedLabel}`;

    this.tabLinksMap[this.recentlyOrderedLabel] = recentlyOrderedLink;

    const loyaltySpecialsLink = this.theme.hasSinglePageMenu ? `menu/single-page#loyalty-specials` : `menu/${this.loyaltySpecialsLabel}`;
    this.tabLinksMap[this.loyaltySpecialsLabel] = loyaltySpecialsLink;
  }
}
